import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { api } from '@/api';
import { RequestHeaders } from '@/common/constants';
import { API_URL } from '@/dtos';
import { useCurrentOrganization } from '@/modules/core/hooks';
import { InvoiceStatusType } from '@/modules/employer/components/Contributions/contributions.types';
import { createPathFromIds, getIdsFromPath } from '@/utils';

import { ReplenishmentItemPayload, ReplenishmentPayload } from '../ReplenishmentStatistic.types';
interface InvoiceResponse {
  invoice_id: number;
  invoice_date: string;
  invoice_status: InvoiceStatusType;
}

interface Response {
  previous_invoice_date: string;
  previous_invoice_amount: number;
  current_invoice_date: string;
  current_invoice_amount: number;
  invoices: InvoiceResponse[];
}

const QUERY_KEY = 'GET_REPLENISHMENT_STATISTIC';
const PLAN_QUERY_KEY = 'GET_PLAN_QUERY_KEY';
interface Params {
  date?: Date;
  planId?: string;
  subsidiaryId?: string;
  futureDates?: Date[];
}
const useGetReplenishmentStatisticQuery = ({
  date = new Date(),
  planId,
  futureDates,
  subsidiaryId,
}: Params) => {
  const isPlanMode = useMemo(() => !!planId, [planId]);
  const {
    observingOrganization: { type, id, path },
  } = useCurrentOrganization();
  const fromDate = useMemo(() => {
    const parsedDate = dayjs(date).subtract(3, 'month').startOf('month');
    return parsedDate.toISOString();
  }, [date]);
  const toDate = useMemo(() => {
    const parsedDate = dayjs(date).add(3, 'month').endOf('month');
    return parsedDate.toISOString();
  }, [date]);
  const isSubsidiaryMode = useMemo(() => {
    return !!subsidiaryId;
  }, [subsidiaryId]);

  const subsidiaryOrgPath = useMemo(() => {
    if (!subsidiaryId) {
      return null;
    }
    const ids = getIdsFromPath(path);

    const observedOrganizationPath = createPathFromIds({
      ...ids,
      subsidiaryId: subsidiaryId,
    });
    return observedOrganizationPath;
  }, [path, subsidiaryId]);

  const {
    isLoading,
    isSuccess,
    isLoadingError,
    data: dataList,
  } = useQuery(
    [QUERY_KEY, isPlanMode, type, id, subsidiaryId],
    () =>
      api.get<Response>(API_URL.METRICS_INVOICE_REPLENISHMENT, {
        params: {
          start_date: fromDate,
          end_date: toDate,
        },
        headers: {
          ...(isSubsidiaryMode ? { [RequestHeaders.observedOrganizationPath]: subsidiaryOrgPath } : {}),
        },
      }),
    {
      retry: 0,
    }
  );

  const {
    isLoading: isPlanLoading,
    isSuccess: isPlanSuccess,
    data: planData,
    isLoadingError: isPlanLoadingError,
  } = useQuery(
    [PLAN_QUERY_KEY, isPlanMode, type, id, subsidiaryId],
    () =>
      api.get(API_URL.METRICS_INVOICE_PLAN_LEVEL_REPLENISHMENT, {
        params: {
          start_date: fromDate,
          end_date: toDate,
        },
        headers: {
          ...(isSubsidiaryMode ? { [RequestHeaders.observedOrganizationPath]: subsidiaryOrgPath } : {}),
        },
      }),
    {
      retry: 0,
    }
  );
  const currentPlanData = useMemo(() => {
    if (!isPlanMode) {
      return null;
    }
    const foundData = planData?.data?.find((item) => `${item.plan_id}` === planId);
    if (foundData && foundData.statistics) {
      return foundData.statistics;
    }
    return null;
  }, [isPlanMode, planData?.data, planId]);

  const previousInvoiceId = useMemo(() => {
    const currentDate = isPlanMode ? currentPlanData : dataList?.data;
    if (!currentDate) {
      return null;
    }
    const foundItem = currentDate?.invoices?.find(
      (item) => item.invoice_date === currentDate?.previous_invoice_date
    );
    if (!foundItem) {
      return null;
    }
    return foundItem?.invoice_id;
  }, [currentPlanData, dataList?.data, isPlanMode]);

  const formattedData = useMemo<ReplenishmentPayload | null>(() => {
    const currentDate = isPlanMode ? currentPlanData : dataList?.data;
    if (!currentDate) {
      return null;
    }
    let invoices: ReplenishmentItemPayload[] =
      currentDate?.invoices?.map(
        (item): ReplenishmentItemPayload => ({
          date: dayjs(item.invoice_date).toDate(),
          status: item.invoice_status as InvoiceStatusType,
        })
      ) || [];
    if (futureDates?.length) {
      const synthInvoices: ReplenishmentItemPayload[] = futureDates.map((item) => ({
        date: item,
        status: InvoiceStatusType.OPEN,
      }));
      invoices = [...synthInvoices, ...invoices];
    }
    return {
      previousInvoiceId,
      previousInvoiceAmount: Math.abs(currentDate?.previous_invoice_amount || 0),
      previousInvoiceDate: currentDate?.previous_invoice_date
        ? dayjs(currentDate?.previous_invoice_date).toDate()
        : null,
      currentInvoiceAmount: Math.abs(currentDate?.current_invoice_amount || 0),
      currentInvoiceDate: currentDate?.current_invoice_date
        ? dayjs(currentDate?.current_invoice_date).toDate()
        : null,
      items: invoices,
    };
  }, [currentPlanData, dataList?.data, futureDates, isPlanMode, previousInvoiceId]);

  const isEmpty = useMemo(() => {
    return !formattedData || _.isEmpty(formattedData?.items);
  }, [formattedData]);

  const planIds = useMemo(() => {
    const list = planData?.data || [];
    const ids = list.map((item) => item.plan_id);
    return ids as number[];
  }, [planData?.data]);
  return {
    planIds,
    isLoading: isLoading || isPlanLoading,
    isEmpty,
    isLoadingError: isLoadingError || isPlanLoadingError,
    isSuccess: isPlanSuccess || isSuccess,
    data: formattedData,
  };
};
export default useGetReplenishmentStatisticQuery;
